import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  assembleCellMutation,
  cellStatus,
  deleteCellMutation,
  type FleetCellView,
  type FleetUnitView,
  fleetOverviewQueryKey,
  SlotsMeter,
  useFleetOverview,
} from '@/entities/fleet'
import { listTelephonyPoolsOptions } from '@/entities/telephony'
import { cn, errorMessage, formatDateTime } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card, CardLink } from '@/shared/ui/card'
import { ConfirmButton } from '@/shared/ui/confirm-button'
import { EmptyState } from '@/shared/ui/empty-state'
import { SelectField } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { SlideOver } from '@/shared/ui/slide-over'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'

export const Route = createFileRoute('/cells')({
  component: CellsPage,
  staticData: { crumb: 'Ячейки' },
})

function CellsPage() {
  // Разобранная ячейка — терминальная запись истории: бэк отдаёт её только по
  // include=all, и только там у оператора есть «стереть запись».
  const [showDismantled, setShowDismantled] = useState(false)
  const { cells, units, isPending, isError, error, refetch } = useFleetOverview({
    includeAll: showDismantled,
  })
  const { cellId } = useParams({ strict: false })
  const [assembleOpen, setAssembleOpen] = useState(false)

  const freeUnits = useMemo(
    () => units.filter((u) => !u.cell_id && u.status !== 'deleted'),
    [units],
  )

  return (
    <>
      <PageHeader
        title="Ячейки"
        description="Связки 1×C + N×L, обслуживающие пулы. Железо появляется только под заявку — прогрев и слив живут в её карточке"
        actions={
          <>
            <Button
              aria-pressed={showDismantled}
              variant={showDismantled ? 'primary' : 'ghost'}
              onClick={() => setShowDismantled((v) => !v)}
            >
              <Icon name="trash" size={16} />
              Показать разобранные
            </Button>
            <Button variant="accent" onClick={() => setAssembleOpen(true)}>
              <Icon name="plus" size={18} />
              Собрать ячейку
            </Button>
          </>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить ячейки"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : cells.length === 0 ? (
        <EmptyState
          icon="grid"
          title="Ячеек пока нет"
          description="Собери первую ячейку из свободных юнитов"
          action={
            <Button variant="accent" size="sm" onClick={() => setAssembleOpen(true)}>
              Собрать ячейку
            </Button>
          }
        />
      ) : (
        <div className="flex items-start gap-6">
          <div className="flex w-[400px] shrink-0 flex-col gap-2">
            {cells.map((cell) => {
              const active = cellId === cell.id
              if (cell.status === 'dismantled') {
                return <DismantledCellCard key={cell.id} cell={cell} />
              }
              return (
                <CardLink
                  key={cell.id}
                  to="/cells/$cellId"
                  params={{ cellId: cell.id }}
                  active={active}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-body-sm font-medium">{cell.id}</span>
                    <StatusBadge {...cellStatus(cell.status)} />
                  </div>
                  <p className={cn('mt-0.5 text-body-sm', active ? 'text-white/80' : 'text-muted')}>
                    C: {cell.c_unit_id} · L×{(cell.l_unit_ids ?? []).length}
                  </p>
                  <div className="mt-3">
                    {/* Знаменатель — честные слоты (железо под клампом линий),
                        план показывается маркером: платят за него. */}
                    <SlotsMeter
                      busy={cell.slots_busy ?? 0}
                      total={cell.slots_honest}
                      plan={cell.slots_total}
                      onGradient={active}
                    />
                  </div>
                </CardLink>
              )
            })}
          </div>

          <div className="min-w-0 flex-1">
            <Outlet />
          </div>
        </div>
      )}

      <AssembleSlideOver
        open={assembleOpen}
        onClose={() => setAssembleOpen(false)}
        freeUnits={freeUnits}
      />
    </>
  )
}

// Разобранная ячейка карточки на бэке не имеет (404 by design) — ссылкой она не
// притворяется, а единственное действие над ней — стереть строку из истории.
function DismantledCellCard({ cell }: { cell: FleetCellView }) {
  const qc = useQueryClient()
  const remove = useMutation({
    ...deleteCellMutation(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: fleetOverviewQueryKey() }),
  })

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-body-sm font-medium">{cell.id}</span>
        <StatusBadge {...cellStatus(cell.status)} />
      </div>
      <p className="mt-0.5 text-body-sm text-muted">
        {cell.slots_total} слотов · разобрана {formatDateTime(cell.updated_at)}
      </p>
      <div className="mt-3 flex items-center gap-2">
        <ConfirmButton
          size="sm"
          confirmLabel="Стереть запись?"
          pending={remove.isPending}
          onConfirm={() => remove.mutate({ path: { id: cell.id } })}
        >
          <Icon name="trash" size={15} />
          Стереть запись
        </ConfirmButton>
      </div>
      {remove.isError && (
        <p className="mt-2 text-caption text-danger">{errorMessage(remove.error)}</p>
      )}
    </Card>
  )
}

function AssembleSlideOver({
  open,
  onClose,
  freeUnits,
}: {
  open: boolean
  onClose: () => void
  freeUnits: FleetUnitView[]
}) {
  const qc = useQueryClient()
  const pools = useQuery({ ...listTelephonyPoolsOptions(), enabled: open })
  const [cUnitId, setCUnitId] = useState('')
  const [lUnitIds, setLUnitIds] = useState<string[]>([])
  const [poolId, setPoolId] = useState('')

  const assemble = useMutation({
    ...assembleCellMutation(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fleetOverviewQueryKey() })
      close()
    },
  })

  const close = () => {
    assemble.reset()
    setCUnitId('')
    setLUnitIds([])
    setPoolId('')
    onClose()
  }

  const cUnits = freeUnits.filter((u) => u.class === 'C')
  const lUnits = freeUnits.filter((u) => u.class === 'L')

  const toggle = (list: string[], set: (v: string[]) => void, id: string) => {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  }

  return (
    <SlideOver
      open={open}
      onClose={close}
      title="Собрать ячейку"
      footer={
        <>
          <Button onClick={close}>Отмена</Button>
          <Button
            variant="primary"
            pending={assemble.isPending}
            disabled={!cUnitId || lUnitIds.length === 0 || !poolId}
            onClick={() =>
              assemble.mutate({
                body: { c_unit_id: cUnitId, l_unit_ids: lUnitIds, pool: poolId },
              })
            }
          >
            Собрать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <SelectField
          label="C-юнит"
          required
          value={cUnitId}
          onChange={(e) => setCUnitId(e.target.value)}
        >
          <option value="" disabled>
            {cUnits.length ? 'Выбери голосовой C-юнит' : 'Нет свободных C-юнитов'}
          </option>
          {cUnits.map((u) => (
            <option key={u.id} value={u.id}>
              {u.id} · {u.gpu_model}
            </option>
          ))}
        </SelectField>

        <CheckGroup
          label="L-юниты"
          required
          empty="Нет свободных L-юнитов"
          options={lUnits.map((u) => ({
            id: u.id,
            title: u.id,
            caption: `${u.gpu_model} · ${u.capacity_slots} слотов`,
          }))}
          selected={lUnitIds}
          onToggle={(id) => toggle(lUnitIds, setLUnitIds, id)}
        />

        <SelectField
          label="Пул"
          required
          value={poolId}
          onChange={(e) => setPoolId(e.target.value)}
          hint="Пул у ячейки один и неизменяем: конфиг юнита несёт одно поле pool, движок — один durable-консьюмер. Нужен другой пул — это другая ячейка"
        >
          <option value="" disabled>
            {(pools.data?.data ?? []).length > 0
              ? 'Выбери пул'
              : 'Пулов нет — заведи в разделе «Телефония»'}
          </option>
          {(pools.data?.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {p.id}
            </option>
          ))}
        </SelectField>

        <p className="rounded-control bg-raise px-4 py-2.5 text-caption text-muted">
          Ручная ячейка живёт вне заявок: заданий из очереди она не берёт, а её линии уменьшают
          ёмкость, которую пул продаёт заявкам. Держи её в отдельном пуле демо.
        </p>

        {assemble.isError && (
          <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
            {errorMessage(assemble.error)}
          </p>
        )}
      </div>
    </SlideOver>
  )
}

function CheckGroup({
  label,
  required,
  empty,
  options,
  selected,
  onToggle,
}: {
  label: string
  required?: boolean
  empty: string
  options: Array<{ id: string; title: string; caption: string }>
  selected: string[]
  onToggle: (id: string) => void
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-body-sm text-muted">
        {label}
        {required && <span className="ml-0.5 text-faint">*</span>}
      </legend>
      <div className="flex flex-col gap-1.5">
        {options.length === 0 && <p className="text-body-sm text-faint">{empty}</p>}
        {options.map((option) => {
          const checked = selected.includes(option.id)
          return (
            <label
              key={option.id}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-3 rounded-control px-4 py-3 transition-colors',
                checked ? 'bg-accent/15' : 'bg-raise hover:bg-hover',
              )}
            >
              <span className="min-w-0">
                <span className="block truncate text-body-sm font-medium">{option.title}</span>
                <span className="block truncate text-caption text-faint">{option.caption}</span>
              </span>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(option.id)}
                className="sr-only"
              />
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                  checked ? 'border-accent bg-accent text-accent-fg' : 'border-outline',
                )}
              >
                {checked && <Icon name="check" size={12} strokeWidth={2.4} />}
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
